import { useState } from "react";
import { newClientKey, requestTurn, workflowAction } from "../../../api/threads/threadsApi";
import { useSubmission, type SubmissionState } from "../../../shared/state/useSubmission";

export interface StepRunner {
  readonly state: SubmissionState;
  /** Run or rerun a step now (FR-WF-4), with values changed for this run (ADR-0032, provisional). */
  readonly run: (stepKey: string, inputValues?: Readonly<Record<string, unknown>>) => void;
  /**
   * Skip a step, leave or resume the Workflow, or run it to a step (`run_to`; no step stops
   * that run, F100), with the values typed into the current step (PC-3).
   */
  readonly act: (event: "skipped" | "left" | "resumed" | "run_to", stepKey: string | null, inputValues?: Readonly<Record<string, unknown>>) => void;
}

/** Running a Workflow's steps from the Thread page, with one idempotency key per step and attempt. */
export function useStepRunner(threadId: string, lastEntryNumber: number): StepRunner {
  const { state, submit } = useSubmission();
  const [requestKeys] = useState(() => new Map<string, string>());
  // A new key once that run was requested; a retry after a failure keeps it.
  const keyFor = (stepKey: string) => requestKeys.get(stepKey) ?? requestKeys.set(stepKey, newClientKey()).get(stepKey) ?? newClientKey();

  return {
    state,
    run: (stepKey, inputValues) => {
      submit(async () => {
        await requestTurn(threadId, { kind: "step", stepKey, clientRequestId: keyFor(stepKey), ...(inputValues === undefined ? {} : { inputValues }) });
        requestKeys.delete(stepKey);
      });
    },
    act: (event, stepKey, inputValues) => {
      submit(async () => {
        await workflowAction(threadId, event, stepKey, lastEntryNumber, inputValues);
      });
    },
  };
}
